/**
 * TourWelcomeModal + TourCelebrationModal (v7)
 *
 * Changes from v6:
 *   - Preview list: emojis 22 -> 28px, label text 15 -> 17px
 *   - Tighter gap between list and Start tour button
 */
import { useEffect } from 'react';
import { Check } from 'lucide-react';
import confetti from 'canvas-confetti';
import { isPurpleOn } from '../../utils/purple';
import { isPlanOn } from '../../utils/plan'; // w2_07

const MODAL_Z = 10200;
const CONFETTI_Z = 10300;

const getThemeTokens = (theme) => {
  // Purple (admin-only): lavender button, pink-purple title, lavender confetti.
  if (theme !== 'warm' && theme !== 'blue' && isPurpleOn()) {
    return {
      headerTint: 'linear-gradient(90deg, #fbe3f3 0%, #f1d8f7 30%, #e6d4fa 58%, #d6d9fb 82%, #dfe2fd 100%)',
      accentGradient: 'linear-gradient(90deg, #D6388A 0%, #8B2FE0 100%)',
      solidButton: '#AC8DF2',
      buttonShadow: 'inset 0 1px 0 rgba(255,255,255,0.45), 0 10px 24px -10px rgba(140, 100, 220, 0.55)',
      logoGlow: '0 10px 28px -6px rgba(140, 100, 220, 0.45)',
      confettiColors: ['#AC8DF2', '#C3AEF4', '#8F70E4', '#F3B6DA', '#D6388A'],
    };
  }
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';

  const headerTint = isWarm
    ? 'linear-gradient(135deg, rgba(124, 92, 58, 0.12) 0%, rgba(124, 144, 112, 0.06) 100%)'
    : isPink
    ? 'linear-gradient(135deg, rgba(233, 30, 140, 0.10) 0%, rgba(168, 85, 247, 0.05) 100%)'
    : 'linear-gradient(135deg, rgba(76, 59, 207, 0.10) 0%, rgba(56, 184, 200, 0.05) 100%)';

  const accentGradient = isWarm
    ? 'linear-gradient(90deg, #7C5C3A 0%, #B07D50 50%, #7C9070 100%)'
    : isPink
    ? 'linear-gradient(90deg, #FF3CAC 0%, #a855f7 100%)'
    : 'linear-gradient(90deg, #4C3BCF 0%, #6366f1 50%, #38b8c8 100%)';

  const solidButton = isWarm ? '#2D5A3D' : isPink ? '#e879a0' : '#4C3BCF';
  const buttonShadow = isWarm
    ? '0 10px 24px -10px rgba(45, 90, 61, 0.55)'
    : isPink
    ? '0 10px 24px -10px rgba(233, 30, 140, 0.55)'
    : '0 10px 24px -10px rgba(76, 59, 207, 0.55)';

  const logoGlow = isWarm
    ? '0 10px 28px -6px rgba(124, 92, 58, 0.45)'
    : isPink
    ? '0 10px 28px -6px rgba(233, 30, 140, 0.45)'
    : '0 10px 28px -6px rgba(76, 59, 207, 0.45)';

  const confettiColors = isWarm
    ? ['#7C5C3A', '#5a9e78', '#D9BC8C', '#2D5A3D', '#B07D50']
    : isPink
    ? ['#e879a0', '#e879a0', '#a855f7', '#f472b6', '#fbbf24']
    : ['#4C3BCF', '#6381f0', '#7B8EE8', '#38b8c8', '#a5b4fc'];

  return { headerTint, accentGradient, solidButton, buttonShadow, logoGlow, confettiColors };
};

const fireConfetti = (colors) => {
  try {
    confetti({
      particleCount: 110,
      spread: 78,
      origin: { y: 0.4 },
      colors,
      zIndex: CONFETTI_Z,
    });
  } catch (e) {}
};

const ModalShell = ({ children, onBackdrop }) => (
  <div
    className="fixed inset-0 flex items-end sm:items-center justify-center"
    style={{
      zIndex: MODAL_Z,
      background: 'rgba(0, 0, 0, 0.6)',
      paddingTop: 'env(safe-area-inset-top, 0px)',
    }}
    onClick={onBackdrop}
  >
    <div
      className="bg-white w-full flex flex-col"
      style={{
        maxWidth: '480px',
        maxHeight: '92vh',
        minHeight: typeof window !== 'undefined' && window.innerWidth >= 640 ? 'auto' : '72vh',
        borderTopLeftRadius: '28px',
        borderTopRightRadius: '28px',
        borderBottomLeftRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
        borderBottomRightRadius: typeof window !== 'undefined' && window.innerWidth >= 640 ? '28px' : '0',
        marginBottom: typeof window !== 'undefined' && window.innerWidth >= 640 ? '40px' : '0',
        boxShadow: '0 -10px 40px -10px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>
  </div>
);

const GradientHeader = ({
  tokens, title, emphasisWord, emphasisPhrase, subtitle, logoSize = 72,
}) => (
  <div className="px-6 pt-4 pb-6 text-center flex-shrink-0" style={{ background: tokens.headerTint }}>
    <div className="flex justify-center mb-4">
      <div style={{ width: 36, height: 5, background: 'rgba(0,0,0,0.15)', borderRadius: 3 }} />
    </div>
    <div className="flex justify-center mb-4">
      <img
        src="/logoroundcorners.png"
        alt="Worthly"
        style={{ width: logoSize, height: logoSize, borderRadius: 18, boxShadow: tokens.logoGlow }}
      />
    </div>
    <h2
      style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: 34,
        lineHeight: 1.1,
        color: '#1a1a1a',
        letterSpacing: '-0.015em',
        paddingBottom: 4,
      }}
    >
      {emphasisPhrase ? (
        <span
          style={{
            background: tokens.accentGradient,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontStyle: 'italic',
            display: 'inline-block',
            paddingRight: '0.1em',
            paddingBottom: '0.1em',
          }}
        >
          {emphasisPhrase}
        </span>
      ) : (
        <>
          {title}
          {emphasisWord && ' '}
          {emphasisWord && (
            <span
              style={{
                background: tokens.accentGradient,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontStyle: 'italic',
                display: 'inline-block',
                paddingRight: '0.1em',
                paddingBottom: '0.1em',
              }}
            >
              {emphasisWord}
            </span>
          )}
        </>
      )}
    </h2>
    {subtitle && (
      <p
        style={{
          fontSize: 15,
          color: '#6a6a6a',
          marginTop: 10,
          fontFamily: "'DM Sans', sans-serif",
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

const TourListItem = ({ emoji, label }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      padding: '12px 4px',
    }}
  >
    <span
      style={{
        flexShrink: 0,
        fontSize: 28,
        width: 40,
        textAlign: 'center',
        lineHeight: 1,
      }}
      role="img"
      aria-hidden="true"
    >
      {emoji}
    </span>
    <span
      style={{
        fontSize: 17,
        fontWeight: 500,
        color: '#1f2937',
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {label}
    </span>
  </div>
);

const OldTourWelcomeModal = ({ onStart, onSkip, theme = 'pink' }) => {
  const tokens = getThemeTokens(theme);

  useEffect(() => {
    const t = setTimeout(() => fireConfetti(tokens.confettiColors), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ModalShell>
      <GradientHeader
        tokens={tokens}
        title="Welcome to"
        emphasisWord="Worthly"
        subtitle="Try out a few core features."
      />

      <div className="px-5 pt-4 pb-0 overflow-y-auto flex-1">
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: '#6b7280',
            textTransform: 'uppercase',
            marginBottom: 8,
            paddingLeft: 4,
          }}
        >
          What you'll do
        </div>
        <TourListItem emoji="🎯" label="Set a spending rule" />
        <TourListItem emoji="💵" label="Log your first transaction" />
        <TourListItem emoji="📆" label="Track a day on your no-spend calendar" />
        <TourListItem emoji="🛍️" label="Add to your wishlist" />
      </div>

      <div
        style={{
          padding: '10px 24px 20px',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
          flexShrink: 0,
        }}
      >
        <button
          onClick={onStart}
          className="w-full text-white font-semibold"
          style={{
            background: tokens.solidButton,
            boxShadow: tokens.buttonShadow,
            padding: '15px 24px',
            fontSize: 16,
            fontFamily: "'DM Sans', sans-serif",
            borderRadius: 16,
            WebkitTapHighlightColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
          data-testid="tour-start-btn"
        >
          Start tour
        </button>
        <button
          onClick={onSkip}
          className="w-full text-sm mt-2"
          style={{
            color: '#6a6a6a',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 500,
            padding: '8px',
          }}
          data-testid="tour-skip-welcome-btn"
        >
          Skip for now
        </button>
      </div>
    </ModalShell>
  );
};

const OldTourCelebrationModal = ({ onFinish, theme = 'pink', hasTrialOrPromo = false, onStartTrial }) => {
  const tokens = getThemeTokens(theme);
  const recap = [
    'Added a spending rule',
    'Logged your first transaction',
    'Logged a day in the no-spend calendar',
    'Added to your wishlist',
  ];

  useEffect(() => {
    const t = setTimeout(() => fireConfetti(tokens.confettiColors), 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ModalShell>
      <GradientHeader
        tokens={tokens}
        emphasisPhrase="You're all set"
        subtitle="Here's what you just set up in Worthly"
      />

      <div className="px-5 py-4 overflow-y-auto flex-1">
        {recap.map((item, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '13px 14px',
              background: 'rgba(0,0,0,0.03)',
              borderRadius: 14,
              marginBottom: 8,
            }}
          >
            <div
              style={{
                flexShrink: 0,
                width: 28,
                height: 28,
                borderRadius: 999,
                background: tokens.solidButton,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Check className="w-4 h-4 text-white" strokeWidth={3} />
            </div>
            <span
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: '#3a3a3a',
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {item}
            </span>
          </div>
        ))}
      </div>

      <div
        style={{
          padding: '0px 24px 24px',
          paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
          flexShrink: 0,
        }}
      >
        <button
          onClick={hasTrialOrPromo ? onFinish : onStartTrial}
          className="w-full text-white font-semibold"
          style={{
            background: tokens.solidButton,
            boxShadow: tokens.buttonShadow,
            padding: '15px 24px',
            fontSize: 16,
            fontFamily: "'DM Sans', sans-serif",
            borderRadius: 16,
            WebkitTapHighlightColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
          data-testid={hasTrialOrPromo ? 'tour-finish-btn' : 'tour-start-trial-btn'}
        >
          Start using Worthly
        </button>
        <p
          style={{
            fontSize: 14,
            color: '#6b7280',
            textAlign: 'center',
            marginTop: 14,
            lineHeight: 1.45,
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 500,
          }}
        >
          Change anything from onboarding in Settings ⚙️
        </p>
      </div>
    </ModalShell>
  );
};

/* ------------------------------------------------------------------ w2_07, w2_08 copy
 * Worthly 2.0 versions of the pop-ups before and after the tour: a white
 * sheet with the app icon, the 2.0 font, lavender ticks and a black button
 * with no glow. The old themes keep the pop-ups above.
 */
const W2_FONT = "'DM Sans', -apple-system, 'Helvetica Neue', sans-serif"; // w2_11
const W2_CONFETTI = ['#B9A6F0', '#8E74E3', '#F2AFC8', '#9ED9B6', '#A9C6F0'];

const W2Sheet = ({ children }) => (
  <div
    className="fixed inset-0 flex items-end justify-center"
    style={{ zIndex: MODAL_Z, background: 'rgba(35, 30, 45, 0.38)', paddingTop: 'env(safe-area-inset-top, 0px)', fontFamily: W2_FONT }}
  >
    <div
      className="w-full flex flex-col"
      style={{ maxWidth: 480, maxHeight: '92vh', background: '#FFFFFF', borderRadius: '28px 28px 0 0', overflow: 'hidden', color: '#25222B' }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{ width: 40, height: 5, borderRadius: 9, background: '#DCD5EC', margin: '10px auto 0', flexShrink: 0 }} />
      {children}
    </div>
  </div>
);

const W2Logo = () => (
  <img
    src="/logoroundcorners.png"
    alt="Worthly"
    style={{ display: 'block', width: 68, height: 68, borderRadius: 18, margin: '18px auto 0', boxShadow: '0 8px 22px rgba(120, 100, 190, 0.18)' }}
  />
);

const w2Title = { margin: '14px 0 0', fontSize: 26, fontWeight: 600, letterSpacing: '-0.04em', textAlign: 'center' };
const w2Sub = { margin: '6px 0 0', fontSize: 14, color: '#8E8A99', textAlign: 'center' };
const w2BlackBtn = { display: 'block', width: '100%', border: 0, borderRadius: 999, padding: 15, fontSize: 15, fontWeight: 600, background: '#1F1D24', color: '#FFFFFF', fontFamily: W2_FONT, boxShadow: 'none', WebkitTapHighlightColor: 'transparent', cursor: 'pointer' };
const w2Foot = { padding: '6px 20px', paddingBottom: 'calc(18px + env(safe-area-inset-bottom))', flexShrink: 0 };

const W2TourWelcome = ({ onStart, onSkip }) => {
  useEffect(() => {
    const t = setTimeout(() => fireConfetti(W2_CONFETTI), 220);
    return () => clearTimeout(t);
  }, []);
  const items = [
    ['\u{1F3AF}', '#ECE6FA', 'Set a spending rule'],
    ['\u{1F4B5}', '#DFF2E7', 'Log your first transaction'],
    ['\u{1F4C6}', '#DFE9F9', 'Track a day on your no-spend calendar'],
    ['\u{1F6CD}\u{FE0F}', '#FCF3D2', 'Add to your wishlist'],
  ];
  return (
    <W2Sheet>
      <div className="overflow-y-auto" style={{ padding: '0 20px', flex: 1 }}>
        <W2Logo />
        <h2 style={w2Title}>Welcome to Worthly!</h2>
        <p style={w2Sub}>Try out a few things you'll use the most.</p>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#ABA6B8', letterSpacing: '0.05em', margin: '22px 2px 6px' }}>WHAT YOU'LL DO</div>
        {items.map(([e, bg, label]) => (
          <div key={label} className="flex items-center" style={{ gap: 14, padding: '9px 2px' }}>
            <span className="flex items-center justify-center flex-shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: bg, fontSize: 19 }}>{e}</span>
            <span style={{ fontSize: 15, fontWeight: 500 }}>{label}</span>
          </div>
        ))}
      </div>
      <div style={w2Foot}>
        <button type="button" onClick={onStart} style={{ ...w2BlackBtn, marginTop: 12 }} data-testid="tour-start-btn">Start tour</button>
        <button type="button" onClick={onSkip} style={{ display: 'block', width: '100%', border: 0, background: 'none', padding: '12px 0 0', fontSize: 13, color: '#8E8A99', fontFamily: W2_FONT, cursor: 'pointer' }} data-testid="tour-skip-welcome-btn">Skip for now</button>
      </div>
    </W2Sheet>
  );
};

const W2TourCelebration = ({ onFinish, hasTrialOrPromo = false, onStartTrial }) => {
  useEffect(() => {
    const t = setTimeout(() => fireConfetti(W2_CONFETTI), 220);
    return () => clearTimeout(t);
  }, []);
  const recap = ['Added a spending rule', 'Logged your first transaction', 'Tracked a day on your calendar', 'Added to your wishlist'];
  return (
    <W2Sheet>
      <div className="overflow-y-auto" style={{ padding: '0 20px', flex: 1 }}>
        <W2Logo />
        <h2 style={w2Title}>Nice work!</h2>
        <p style={w2Sub}>Here's what you just did in Worthly.</p>
        <div style={{ marginTop: 18 }}>
          {recap.map((item) => (
            <div key={item} className="flex items-center" style={{ gap: 12, padding: '12px 14px', background: '#F7F5FB', borderRadius: 16, marginTop: 8, fontSize: 14.5, fontWeight: 500 }}>
              <span className="flex items-center justify-center flex-shrink-0" style={{ width: 24, height: 24, borderRadius: 999, background: '#8E74E3', color: '#FFFFFF' }}>
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
              </span>
              {item}
            </div>
          ))}
        </div>
      </div>
      <div style={w2Foot}>
        <button type="button" onClick={hasTrialOrPromo ? onFinish : onStartTrial} style={{ ...w2BlackBtn, marginTop: 14 }} data-testid={hasTrialOrPromo ? 'tour-finish-btn' : 'tour-start-trial-btn'}>Start using Worthly</button>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: '#8E8A99', textAlign: 'center' }}>You can change anything from setup in Settings {'⚙️'}</p>
      </div>
    </W2Sheet>
  );
};

export const TourWelcomeModal = (props) => (isPlanOn() ? <W2TourWelcome {...props} /> : <OldTourWelcomeModal {...props} />);
export const TourCelebrationModal = (props) => (isPlanOn() ? <W2TourCelebration {...props} /> : <OldTourCelebrationModal {...props} />);
